import React from 'react';
import Tooltip from './Tooltip';
import { effectRegistry } from '../services/effectRegistry';

/**
 * [PHASE 2.3 - NOV 15, 2025]: Effect Breakdown Tooltip Component
 * [PURPOSE]: Shows detailed stat calculations with visual effect breakdowns
 * [WHY]: Players need transparency on how stats are calculated from all sources
 * [INTEGRATION]: Wraps any stat display, fetches breakdown from EffectRegistry
 *
 * Design Philosophy:
 * - TRANSPARENT: Show ALL sources affecting a stat
 * - VISUAL: Color-code by source type (ability=blue, item=green, spell=purple)
 * - INFORMATIVE: Show formula, total, dice bonuses, conditionals
 * - BEAUTIFUL: Match existing tooltip design language
 * - ACCESSIBLE: Works with existing Tooltip component auto-positioning
 *
 * @param {string} stat - Stat type ('ac', 'initiative', 'speed', 'passivePerception', etc.)
 * @param {number} baseValue - Base value before effects
 * @param {object} character - Character object
 * @param {array} inventory - Inventory array
 * @param {array} activeQuickBonuses - Active spell effects/buffs
 * @param {object} context - Additional context for conditionals
 * @param {ReactNode} children - Stat display element to wrap
 * @param {string} maxWidth - Tooltip max width (default: '320px')
 * @param {string} position - Tooltip position (default: 'auto')
 *
 * @example
 * <EffectBreakdownTooltip
 *   stat="ac"
 *   baseValue={10}
 *   character={character}
 *   inventory={inventory}
 *   activeQuickBonuses={activeQuickBonuses}
 * >
 *   <div className="text-3xl font-bold">{totalAC}</div>
 * </EffectBreakdownTooltip>
 */
const EffectBreakdownTooltip = ({
  stat,
  baseValue,
  character,
  inventory,
  activeQuickBonuses,
  context = {},
  children,
  maxWidth = '320px',
  position = 'auto'
}) => {
  // [STEP 1]: Get formatted breakdown from EffectRegistry
  // [INTEGRATION]: Passes character, inventory, and quick bonuses for full calculation
  const breakdown = effectRegistry.getBreakdownForDisplay(
    stat,
    baseValue,
    character,
    { ...context, inventory, activeQuickBonuses }
  );

  // [STEP 2]: Build tooltip content JSX
  // [VISUAL DESIGN]: Matches VitalStatsCard AC tooltip style
  const tooltipContent = (
    <div className="p-2">
      {/* [HEADER]: Stat name with border separator */}
      <div className="text-sm font-bold text-white mb-2 border-b border-gray-600 pb-1">
        {formatStatName(stat)} Breakdown
      </div>

      {/* [BASE VALUE]: Always show base value first */}
      <div className="flex justify-between items-center mb-1">
        <span className={breakdown.base.color}>
          {breakdown.base.label}:
        </span>
        <span className="text-white font-mono">
          {breakdown.base.value}
        </span>
      </div>

      {/* [EFFECTS LIST]: All modifiers/bonuses affecting this stat */}
      {breakdown.effects.length > 0 && (
        <div className="space-y-1 my-2">
          {breakdown.effects.map((effect, idx) => (
            <div key={idx} className="flex justify-between items-center">
              {/* [SOURCE]: Icon + name with color coding */}
              <span className={`flex items-center gap-1 ${effect.color}`}>
                <span>{effect.icon}</span>
                <span className="text-xs">{effect.source}</span>
                {/* [CONDITIONAL FLAG]: Yellow indicator for conditional effects */}
                {effect.conditional && (
                  <span className="text-[10px] text-yellow-400">(conditional)</span>
                )}
              </span>
              {/* [MODIFIER]: +3, ×2, 1d4, etc. */}
              <span className={`font-mono font-semibold ${effect.color}`}>
                {effect.modifier}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* [NO EFFECTS]: Show when only base value (no bonuses) */}
      {breakdown.effects.length === 0 && (
        <div className="text-xs text-gray-500 italic my-2">
          No active effects
        </div>
      )}

      {/* [STACKING PREVENTION]: Show which spells were blocked */}
      {breakdown.stackingPrevented?.length > 0 && (
        <div className="mt-2 p-2 bg-yellow-900/30 border border-yellow-600/50 rounded text-[10px]">
          <div className="text-yellow-300 font-semibold mb-1">
            ⚠️ Stacking Prevented:
          </div>
          {breakdown.stackingPrevented.map((effect, idx) => (
            <div key={idx} className="text-yellow-400">
              {effect.spellName} (would not stack with active effect)
            </div>
          ))}
        </div>
      )}

      {/* [TOTAL]: Final calculated value */}
      <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-600">
        <span className="text-white font-bold">Total:</span>
        <span className="text-white font-mono font-bold text-lg">
          {breakdown.total}
          {/* [DICE INDICATOR]: Show "+dice" if dice bonuses present */}
          {breakdown.hasDice && (
            <span className="text-purple-400 ml-1">
              + dice
            </span>
          )}
        </span>
      </div>

      {/* [FORMULA]: Nerdy math formula for players who want details */}
      <div className="mt-2 text-[10px] text-gray-500 font-mono text-center">
        {breakdown.formula}
      </div>

      {/* [CONDITIONALS NOTE]: Reminder that some effects have conditions */}
      {breakdown.hasConditionals && (
        <div className="mt-2 text-[10px] text-yellow-400 italic">
          * Some effects have conditions
        </div>
      )}
    </div>
  );

  // [STEP 3]: Wrap children with Tooltip component
  // [INTEGRATION]: Uses existing god-tier Tooltip component
  return (
    <Tooltip
      content={tooltipContent}
      position={position}
      maxWidth={maxWidth}
    >
      {children}
    </Tooltip>
  );
};

/**
 * [HELPER]: Format stat names for display headers
 * [PURPOSE]: Convert technical stat IDs to user-friendly names
 * [WHY]: "ac" → "Armor Class", "passivePerception" → "Passive Perception"
 */
const formatStatName = (stat) => {
  const names = {
    'ac': 'Armor Class',
    'initiative': 'Initiative',
    'speed': 'Speed',
    'passivePerception': 'Passive Perception',
    'proficiencyBonus': 'Proficiency Bonus',
    'spellAttackBonus': 'Spell Attack Bonus',
    'spellSaveDC': 'Spell Save DC',
    'attackRoll': 'Attack Roll',
    'damageRoll': 'Damage Roll',
    'savingThrow': 'Saving Throw',
    'abilityCheck': 'Ability Check',
    'strength': 'Strength',
    'dexterity': 'Dexterity',
    'constitution': 'Constitution',
    'intelligence': 'Intelligence',
    'wisdom': 'Wisdom',
    'charisma': 'Charisma',
    'maxHP': 'Maximum HP',
    'carryCapacity': 'Carry Capacity'
  };
  return names[stat] || stat.charAt(0).toUpperCase() + stat.slice(1);
};

export default EffectBreakdownTooltip;
